import { inject, Injectable } from '@angular/core';

import { getWorkflowNodeBase } from '@blaze-runtime/workflow-domain';
import {
  type DataBindingSourceInfo,
  updateMergeNodeAssignmentsFromColumns,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { isWorkflowSchema } from '@blaze-shared/schema-domain';
import { type MergeWorkflowNode, type WorkflowNode } from '@blaze-shared/workflow-domain';

/**
 * Builds the `Merge` node that writes a list-shaped form's rows back to its dataset.
 *
 * Regenerating keeps whatever the author customised on the existing node and only refreshes the
 * assignments, so that a column added to the data source appears without discarding their work.
 */
@Injectable({ providedIn: 'root' })
export class MergeActionNodeService {
  private readonly schemaService = inject(SchemaService);

  /** @throws when the data source has no schema to take the target dataset and columns from. */
  getMergeActionNode(
    elementDataSource: DataBindingSourceInfo,
    existingActionNode: MergeWorkflowNode | undefined,
    input: string,
    successMessage?: string,
  ): WorkflowNode {
    const sourceSchema = this.schemaService.getSchema(
      elementDataSource.id,
      elementDataSource.sourceType === 'context',
    );
    if (!sourceSchema) {
      throw new Error('Could not find workflow output schema');
    }

    return {
      ...getWorkflowNodeBase('Merge', 'action'),
      version: 2,
      successMessage,
      input,
      ...existingActionNode,
      datasetId: isWorkflowSchema(sourceSchema) ? sourceSchema.tableId : sourceSchema.id,
      assignments: updateMergeNodeAssignmentsFromColumns(existingActionNode, sourceSchema.columns),
    } as WorkflowNode;
  }
}
